import type { Profile } from '../types';

export const profiles: Profile[] = [
  {
    id: 'doctor',
    name: 'New Attending Doctor',
    role: 'High income, heavy student debt',
    challenge: 'Lifestyle inflation and debt service can quietly eat the whole paycheck.',
    hiddenAdvantage: 'High active income gives you a fast snowball if you resist the upgrade treadmill.',
    cash: 8000,
    activeIncome: 14200,
    passiveIncome: 0,
    expenses: 8200,
    skill: 4,
    jobTime: 6,
    jobLabel: 'Hospital schedule',
    debts: [
      { name: 'Student loans', balance: 285000, payment: 2400, rate: 0.065 },
      { name: 'Car note', balance: 38000, payment: 740, rate: 0.078 },
      { name: 'Credit card', balance: 9000, payment: 310, rate: 0.219 },
    ],
    assets: [{ name: 'Retirement index fund', value: 18000, passive: 0, sellable: true }],
  },
  {
    id: 'plumber',
    name: 'Journeyman Plumber',
    role: 'Skilled trade, no business cushion',
    challenge: 'You earn well by the hour, but no capital or business systems means every surprise matters.',
    hiddenAdvantage: 'Your craft can become a route, crew, or service business once you build owner skills.',
    cash: 1200,
    activeIncome: 6400,
    passiveIncome: 0,
    expenses: 4100,
    skill: 4,
    jobTime: 5,
    jobLabel: 'Plumbing job',
    debts: [
      { name: 'Truck loan', balance: 21000, payment: 480, rate: 0.083 },
      { name: 'Tool financing', balance: 6200, payment: 210, rate: 0.12 },
    ],
    assets: [{ name: 'Paid tools', value: 8500, passive: 0, sellable: true }],
  },
  {
    id: 'barista',
    name: 'Beauty School Dropout',
    role: 'Minimum wage, flexible schedule',
    challenge: 'Low income makes every dollar feel too small to matter.',
    hiddenAdvantage: 'Low fixed expenses and flexible time make skill-building and side hustles powerful.',
    cash: 450,
    activeIncome: 2100,
    passiveIncome: 0,
    expenses: 1600,
    skill: 2,
    jobTime: 4,
    jobLabel: 'Starbucks shifts',
    debts: [{ name: 'Credit card', balance: 2400, payment: 90, rate: 0.24 }],
    assets: [],
  },
  {
    id: 'sales',
    name: 'Commission Sales Rep',
    role: 'Variable income, high temptation',
    challenge: 'Good months invite big lifestyle decisions that become bad-month obligations.',
    hiddenAdvantage: 'Upside income can rapidly fund assets when you bank the bonuses.',
    cash: 3600,
    activeIncome: 5800,
    passiveIncome: 12,
    expenses: 4700,
    skill: 5,
    jobTime: 5,
    jobLabel: 'Sales job',
    debts: [
      { name: 'Credit card', balance: 7700, payment: 240, rate: 0.22 },
      { name: 'Car lease', balance: 0, payment: 610, rate: 0 },
    ],
    assets: [{ name: 'Taxable brokerage', value: 4200, passive: 12, sellable: true }],
  },
  {
    id: 'manager',
    name: 'Corporate Manager',
    role: 'Strong salary, expensive life',
    challenge: 'You look successful, but the run rate keeps moving the FIRE line away.',
    hiddenAdvantage: 'Management skills transfer well into buying and systematizing small businesses.',
    cash: 9800,
    activeIncome: 9200,
    passiveIncome: 0,
    expenses: 7600,
    skill: 6,
    jobTime: 6,
    jobLabel: 'Management job',
    debts: [
      { name: 'Student loans', balance: 42000, payment: 520, rate: 0.052 },
      { name: 'Credit card', balance: 12500, payment: 360, rate: 0.205 },
    ],
    assets: [{ name: '401k balance', value: 61000, passive: 0, sellable: false }],
  },
  {
    id: 'single-parent',
    name: 'Single Parent Nurse',
    role: 'Stable income, tight time',
    challenge: 'Time is scarce, childcare is real, and emergencies hit hard.',
    hiddenAdvantage: 'Discipline and stability create an excellent base for boring wealth.',
    cash: 2600,
    activeIncome: 6100,
    passiveIncome: 6,
    expenses: 4300,
    skill: 4,
    jobTime: 5,
    jobLabel: 'Nursing job',
    defaultFamily: { status: 'single', kids: 2 },
    debts: [
      { name: 'Student loans', balance: 31000, payment: 310, rate: 0.049 },
      { name: 'Car note', balance: 12600, payment: 310, rate: 0.071 },
    ],
    assets: [{ name: 'Emergency fund CD', value: 1800, passive: 6, sellable: true }],
  },
  {
    id: 'teacher',
    name: 'Public School Teacher',
    role: 'Stable income, low upside without a plan',
    challenge: 'A reliable paycheck can hide the fact that there is not much margin.',
    hiddenAdvantage: 'Summer flexibility and trust-based skills make tutoring, curriculum, and small service businesses realistic.',
    cash: 1900,
    activeIncome: 4300,
    passiveIncome: 0,
    expenses: 3100,
    skill: 4,
    jobTime: 5,
    jobLabel: 'Teaching job',
    debts: [
      { name: 'Student loans', balance: 27000, payment: 260, rate: 0.052 },
      { name: 'Credit card', balance: 3800, payment: 130, rate: 0.219 },
    ],
    assets: [{ name: 'Pension credit', value: 12000, passive: 0, sellable: false }],
  },
  {
    id: 'developer',
    name: 'Burned-Out Software Developer',
    role: 'High salary, low time and energy',
    challenge: 'Strong income is paired with golden handcuffs and recovery time.',
    hiddenAdvantage: 'Technical skill can create scalable assets if you buy back time.',
    cash: 14000,
    activeIncome: 11800,
    passiveIncome: 120,
    expenses: 9400,
    skill: 6,
    jobTime: 6,
    jobLabel: 'Software job',
    debts: [{ name: 'Car note', balance: 24000, payment: 510, rate: 0.069 }],
    assets: [{ name: 'Index portfolio', value: 76000, passive: 120, sellable: true }],
  },
  {
    id: 'server',
    name: 'Restaurant Server',
    role: 'Tip income, flexible schedule',
    challenge: 'Income swings and cash leaks make it hard to see the real statement.',
    hiddenAdvantage: 'Flexible hours and people skills can turn into sales, events, or service routes.',
    cash: 900,
    activeIncome: 3100,
    passiveIncome: 0,
    expenses: 2400,
    skill: 3,
    jobTime: 4,
    jobLabel: 'Restaurant shifts',
    debts: [{ name: 'Credit card', balance: 5100, payment: 170, rate: 0.239 }],
    assets: [],
  },
  {
    id: 'electrician',
    name: 'Licensed Electrician',
    role: 'Skilled trade, path to crew ownership',
    challenge: 'Side jobs pay well but can eat every weekend.',
    hiddenAdvantage: 'Licensing, reputation, and referrals can become a real service business once you add owner skills.',
    cash: 5200,
    activeIncome: 7200,
    passiveIncome: 0,
    expenses: 4600,
    skill: 5,
    jobTime: 5,
    jobLabel: 'Electrical work',
    debts: [
      { name: 'Work van', balance: 26000, payment: 540, rate: 0.082 },
      { name: 'Tool account', balance: 4500, payment: 180, rate: 0.11 },
    ],
    assets: [{ name: 'Tools and license', value: 16000, passive: 0, sellable: true }],
  },
  {
    id: 'military',
    name: 'Recently Separated Veteran',
    role: 'Benefits, transition uncertainty',
    challenge: 'The next income engine is not obvious yet.',
    hiddenAdvantage: 'Discipline, benefits, and operations experience travel well into boring businesses.',
    cash: 6500,
    activeIncome: 3600,
    passiveIncome: 900,
    expenses: 3600,
    skill: 5,
    jobTime: 3,
    jobLabel: 'Transition work',
    debts: [{ name: 'Credit card', balance: 2200, payment: 80, rate: 0.21 }],
    assets: [{ name: 'VA disability benefit stream', value: 0, passive: 900, sellable: false }],
  },
  {
    id: 'artist',
    name: 'Freelance Designer',
    role: 'Creative upside, unstable pipeline',
    challenge: 'Great months and scary months average out worse than they feel.',
    hiddenAdvantage: 'Creative assets can be productized into templates, courses, retainers, and licensing.',
    cash: 2800,
    activeIncome: 4700,
    passiveIncome: 30,
    expenses: 3300,
    skill: 6,
    jobTime: 4,
    jobLabel: 'Client work',
    debts: [{ name: 'Laptop financing', balance: 1800, payment: 90, rate: 0.15 }],
    assets: [{ name: 'Template store', value: 2500, passive: 30, sellable: true, recurringTime: 1 }],
  },
];
